import {
  AnvaQuote,
  AnvaQuoteWithJob,
  AnvaPackageWithJob,
  AnvaJobStatus,
} from '@flows/src/types/anva';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  useQueryClient,
} from 'react-query';
import { ErrorResponse } from '../../../../../types/API';
import { wgApi } from '../../../../app/src/features/api/wgApi';
import { useCallback } from 'react';

interface Params {
  sessionId?: string;
  queryOptions?: UseQueryOptions<AnvaQuotesV2Response>;
}

export type AnvaQuotesV2Response = {
  quotes: AnvaQuoteWithJob[];
  packages: AnvaPackageWithJob[];
};

export type AnvaJobErrorResult = {
  title?: string | null;
  message?: string;
};

export type AnvaJobResultResponse = {
  id: string;
  created_at: string;
  finished_at?: string;
  procrastinate_job_id?: number | null;
  type: string;
  status: AnvaJobStatus;
  result?: AnvaQuote | AnvaJobErrorResult;
};

// Type guards to differentiate between success and error results
export const isAnvaQuote = (result: unknown): result is AnvaQuote => {
  return (
    typeof result === 'object' &&
    result !== null &&
    'id' in result &&
    'name' in result
  );
};

export const isJobErrorResult = (
  result: unknown,
): result is AnvaJobErrorResult => {
  return (
    typeof result === 'object' &&
    result !== null &&
    ('title' in result || 'message' in result) &&
    !('id' in result)
  );
};

export interface AnvaQuotesPayload {
  chosen_insurance_packages?: {
    name: string;
    code: string;
    due_date?: string;
    payment_interval?: string | null;
    chosen_insurance_policies: {
      anva_product_id: string;
      index: number;
      coverages: number[];
    }[];
  }[];
  chosen_insurance_policies?: {
    anva_product_id: string;
    index: number;
    coverages: number[];
    due_date?: string;
    payment_interval?: string | null;
  }[];
}

// V2 API call - Returns quotes with job information
export const getAnvaQuotesV2 = async (
  sessionId: string,
  payload?: AnvaQuotesPayload,
) => {
  const { data, isError } = await wgApi.post<AnvaQuotesV2Response>(
    `/v2/api/anvaflows/${sessionId}/calculate`,
    payload,
  );
  if (isError) throw new Error(JSON.stringify(data as ErrorResponse));
  return data;
};

// Poll job result
export const getJobResult = async (jobId: string) => {
  const { data, isError } = await wgApi.get<AnvaJobResultResponse>(
    `/v1/api/flows/jobs/${jobId}`,
  );
  if (isError) throw new Error(JSON.stringify(data as ErrorResponse));
  return data;
};

export const getUseAnvaQuotesV2QKey = (sessionId: string) => [
  'flow-products',
  'anva',
  'quotes-v2',
  sessionId,
];

export const getJobResultQKey = (jobId: string) => [
  'flow-products',
  'anva',
  'job-result',
  jobId,
];

export const useAnvaQuotesV2 = ({ sessionId, queryOptions }: Params) => {
  return useQuery<AnvaQuotesV2Response>(
    getUseAnvaQuotesV2QKey(sessionId!),
    () => getAnvaQuotesV2(sessionId!),
    {
      refetchOnMount: false,
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!sessionId,
      ...queryOptions,
    },
  );
};

export const useJobResult = (jobId: string, enabled = true) => {
  return useQuery<AnvaJobResultResponse>(
    getJobResultQKey(jobId),
    () => getJobResult(jobId),
    {
      refetchOnMount: false,
      retry: false,
      refetchOnWindowFocus: false,
      enabled: enabled && !!jobId,
      refetchInterval: false, // We'll handle polling manually
    },
  );
};

export const useAnvaQuotesV2Calculate = () => {
  return useMutation<
    AnvaQuotesV2Response,
    unknown,
    { sessionId: string; payload: AnvaQuotesPayload }
  >(({ sessionId, payload }) => getAnvaQuotesV2(sessionId, payload));
};

// Simple hook for sidebar that just needs initial tag data
export const useAnvaQuotesV2Simple = ({
  sessionId,
  enabled = true,
}: {
  sessionId?: string;
  enabled?: boolean;
}) => {
  return useQuery(
    getUseAnvaQuotesV2QKey(sessionId!),
    () => getAnvaQuotesV2(sessionId!),
    {
      enabled: enabled && !!sessionId,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

// Cache key for completed quotes (separate from initial job response)
export const getCompletedQuotesQKey = (sessionId: string) => [
  'flow-products',
  'anva',
  'completed-quotes',
  sessionId,
];

// Function to get cached completed quotes
export const useCompletedQuotesCache = (sessionId?: string) => {
  const queryClient = useQueryClient();

  const getCachedQuotes = useCallback(() => {
    if (!sessionId) return null;
    return queryClient.getQueryData<AnvaQuote[]>(
      getCompletedQuotesQKey(sessionId),
    );
  }, [sessionId, queryClient]);

  const setCachedQuotes = useCallback(
    (quotes: AnvaQuote[]) => {
      if (!sessionId) return;
      queryClient.setQueryData(getCompletedQuotesQKey(sessionId), quotes);
    },
    [sessionId, queryClient],
  );

  const clearCachedQuotes = useCallback(() => {
    if (!sessionId) return;
    queryClient.removeQueries(getCompletedQuotesQKey(sessionId));
  }, [sessionId, queryClient]);

  return {
    getCachedQuotes,
    setCachedQuotes,
    clearCachedQuotes,
  };
};
